/**
 * Implementation of vConsole Output Log Plugin
 * Based on the official plugin from https://github.com/sunlanda/vconsole-outputlog-plugin
 * Properly implements VConsole Plugin API
 */

import copy from 'copy-to-clipboard';
 
class VConsoleOutputLogsPlugin {
  constructor(vConsole) {
    this.vConsole = vConsole;
    this.isReady = false;
    
    console.log('[VConsole-OutputLog] Plugin constructor called');
    console.log('[VConsole-OutputLog] NOTE: This plugin adds Copy/Download buttons to the Log tab toolbar');
    console.log('[VConsole-OutputLog] It does NOT create a new menu item next to Log/System/Network');
    
    // Try to add buttons with multiple attempts at different times
    // to ensure vConsole DOM is ready
    this.attemptAddingButtons(100);
    this.attemptAddingButtons(1000);
    this.attemptAddingButtons(3000);
    
    return this;
  }

  // Try adding buttons with a specific delay
  attemptAddingButtons(delay) {
    setTimeout(() => {
      if (!this.isReady) {
        console.log(`[VConsole-OutputLog] Attempting to add buttons (delay: ${delay}ms)`);
        this.addButtonsDirectly();
      }
    }, delay);
  }
  
  addButtonsDirectly() {
    try {
      // Log all vConsole related elements
      console.log('[VConsole-OutputLog] Searching for vConsole elements...');
      
      const vcElements = document.querySelectorAll('[class*="vc-"]');
      console.log(`[VConsole-OutputLog] Found ${vcElements.length} vConsole elements`);
      
      // List all unique class names to understand the DOM structure
      const uniqueClasses = new Set();
      vcElements.forEach(el => {
        if (el.className) {
          uniqueClasses.add(el.className);
        }
      });
      console.log('[VConsole-OutputLog] Unique vConsonsole element classes:', Array.from(uniqueClasses).sort());
      
      // Check if vConsole is open
      const vconsoleElement = document.querySelector('#__vconsole');
      if (vconsoleElement) {
        console.log('[VConsole-OutputLog] vConsole element found:', vconsoleElement.className);
        console.log('[VConsole-OutputLog] vConsole is active:', vconsoleElement.classList.contains('vc-toggle'));
      } else {
        console.log('[VConsole-OutputLog] vConsole element not found, might not be initialized yet');
        return; // Exit early if vConsole isn't ready
      }
      
      // CRITICAL: Find the Log panel specifically (not just any active panel)
      // We need to target the DEFAULT tab's content area
      const logPanelSelectors = [
        '#__vconsole .vc-log',           // The log panel container
        '.vc-tab-content[data-tab="default"]',  // Default tab content
        '#__vconsole .vc-content',       // General content area
        '.vc-content'                    // Fallback
      ];
      
      console.log('[VConsole-OutputLog] Searching for log panel with these selectors:', logPanelSelectors);
      
      let logPanel = null;
      
      for (const selector of logPanelSelectors) {
        const element = document.querySelector(selector);
        console.log(`[VConsole-OutputLog] Trying selector "${selector}": ${element ? 'FOUND' : 'not found'}`);
        if (element) {
          console.log(`[VConsole-OutputLog] ✅ Found log panel with selector: ${selector}`);
          console.log(`[VConsole-OutputLog] Panel class: ${element.className}`);
          console.log(`[VConsole-OutputLog] Panel ID: ${element.id || 'none'}`);
          console.log(`[VConsole-OutputLog] Panel has ${element.children.length} children`);
          console.log(`[VConsole-OutputLog] Panel children classes:`, Array.from(element.children).map(c => c.className).slice(0, 5));
          logPanel = element;
          break;
        }
      }
      
      if (!logPanel) {
        console.log('[VConsole-OutputLog] ❌ Could not find log panel with any selector');
        console.log('[VConsole-OutputLog] Available panels:', document.querySelectorAll('.vc-panel, .vc-tab, [class*="panel"]'));
        return; // Exit without setting initialized
      }
      
      // Check if we already added our toolbar
      let toolbar = logPanel.querySelector('.output-log-toolbar');
      
      // Also check if buttons already exist anywhere in the log panel
      if (logPanel.querySelector('.vc-tool-copy-btn') || logPanel.querySelector('.vc-tool-export-btn')) {
        console.log('[VConsole-OutputLog] Buttons already exist in log panel, skipping');
        this.isReady = true;
        return;
      }
      
      // If no toolbar found, create one at the TOP of the log panel
      if (!toolbar) {
        console.log('[VConsole-OutputLog] Creating toolbar for log panel...');
        
        // Create toolbar with very visible styling
        toolbar = document.createElement('div');
        toolbar.className = 'vc-toolbar output-log-toolbar';
        toolbar.style.cssText = `
          display: flex !important;
          justify-content: flex-end !important;
          align-items: center !important;
          padding: 10px !important;
          margin: 0 !important;
          border-bottom: 2px solid #333 !important;
          background: #1e1e1e !important;
          z-index: 1000 !important;
          position: sticky !important;
          top: 0 !important;
          width: 100% !important;
          box-sizing: border-box !important;
        `;
        
        console.log('[VConsole-OutputLog] Inserting toolbar at beginning of log panel');
        console.log('[VConsole-OutputLog] Log panel first child:', logPanel.firstChild ? logPanel.firstChild.className : 'none');
        
        // Add to beginning of log panel
        if (logPanel.firstChild) {
          logPanel.insertBefore(toolbar, logPanel.firstChild);
        } else {
          logPanel.appendChild(toolbar);
        }
        
        console.log('[VConsole-OutputLog] Toolbar created and inserted');
        console.log('[VConsole-OutputLog] Toolbar parent is now:', toolbar.parentElement ? toolbar.parentElement.className : 'no parent');
      }
      
      // Add copy button - with VERY visible styling
      const copyBtn = document.createElement('button');
      copyBtn.className = 'vc-tool vc-tool-copy vc-tool-copy-btn';
      copyBtn.textContent = '📋 Copy Logs';
      copyBtn.style.cssText = `
        margin-right: 10px !important;
        padding: 8px 15px !important;
        background: #28a745 !important;
        color: white !important;
        border: 2px solid #1e7e34 !important;
        border-radius: 4px !important;
        cursor: pointer !important;
        font-size: 14px !important;
        font-weight: bold !important;
        display: inline-block !important;
      `;
      copyBtn.addEventListener('click', this.copyText.bind(this));
      toolbar.appendChild(copyBtn);
      
      console.log('[VConsole-OutputLog] Copy button added, style:', copyBtn.style.cssText);
      
      // Add export button - with VERY visible styling
      const exportBtn = document.createElement('button');
      exportBtn.className = 'vc-tool vc-tool-export vc-tool-export-btn';
      exportBtn.textContent = '💾 Download';
      exportBtn.style.cssText = `
        padding: 8px 15px !important;
        background: #007bff !important;
        color: white !important;
        border: 2px solid #0056b3 !important;
        border-radius: 4px !important;
        cursor: pointer !important;
        font-size: 14px !important;
        font-weight: bold !important;
        display: inline-block !important;
      `;
      exportBtn.addEventListener('click', this.export.bind(this));
      toolbar.appendChild(exportBtn);
      
      console.log('[VConsole-OutputLog] Export button added, style:', exportBtn.style.cssText);
      
      console.log('[VConsole-OutputLog] ✅ Buttons added successfully!');
      console.log('[VConsole-OutputLog] Toolbar in DOM:', document.contains(toolbar));
      console.log('[VConsole-OutputLog] Toolbar parent:', toolbar.parentElement ? toolbar.parentElement.className : 'no parent');
      console.log('[VConsole-OutputLog] Toolbar children count:', toolbar.children.length);
      console.log('[VConsole-OutputLog] Toolbar offsetHeight:', toolbar.offsetHeight);
      console.log('[VConsole-OutputLog] Toolbar offsetWidth:', toolbar.offsetWidth);
      console.log('[VConsole-OutputLog] Copy button offsetHeight:', copyBtn.offsetHeight);
      console.log('[VConsole-OutputLog] Copy button offsetWidth:', copyBtn.offsetWidth);
      
      this.isReady = true;
    } catch (err) {
      console.error('[VConsole-OutputLog] Failed to add buttons:', err);
    }
  }
  
  funDownload = (content, filename) => {
    var eleLink = document.createElement("a");
    eleLink.download = filename;
    eleLink.style.display = "none";
    var blob = new Blob([content]);
    eleLink.href = URL.createObjectURL(blob);
    document.body.appendChild(eleLink);
    eleLink.click();
    document.body.removeChild(eleLink);
  }
  
  export = () => {
    try {
      console.log('[VConsole-OutputLog] Export button clicked');
      
      // Try multiple selectors to find logs
      const logSelectors = [
        ".vc-content .vc-log",
        ".vc-log-box",
        ".vc-log",
        ".vc-item",
        ".vc-console",
        "#__vconsole .vc-log"
      ];
      
      let logElement = null;
      for (const selector of logSelectors) {
        const element = document.querySelector(selector);
        if (element) {
          logElement = element;
          console.log(`[VConsole-OutputLog] Found logs with selector: ${selector}`);
          break;
        }
      }
      
      if (!logElement) {
        console.error('[VConsole-OutputLog] Could not find log element');
        alert('Could not find logs to export!');
        return;
      }
      
      let nodeArr = logElement.children;
      console.log(`[VConsole-OutputLog] Found ${nodeArr.length} log entries`);
      
      let _str = '';
      for (let i = 0; i < nodeArr.length; i++) {
        const ele = nodeArr[i];
        _str += `${ele.textContent}\n`;
      }
      
      const now = new Date();
      const timestamp = now.toISOString().replace(/[:.]/g, '-').substring(0, 19);
      this.funDownload(_str, `pilot-logs-${timestamp}.log`);
      console.log('[VConsole-OutputLog] Logs exported successfully');
      alert('Logs downloaded successfully!');
    } catch (e) {
      console.error('[VConsole-OutputLog] Export failed:', e);
      alert('Error exporting logs: ' + e.message);
    }
  };
  
  copyText = () => {
    try {
      console.log('[VConsole-OutputLog] Copy button clicked');
      
      // Try multiple selectors to find logs
      const logSelectors = [
        ".vc-content .vc-log",
        ".vc-log-box",
        ".vc-log",
        ".vc-item",
        ".vc-console",
        "#__vconsole .vc-log"
      ];
      
      let logElement = null;
      for (const selector of logSelectors) {
        const element = document.querySelector(selector);
        if (element) {
          logElement = element;
          console.log(`[VConsole-OutputLog] Found logs with selector: ${selector}`);
          break;
        }
      }
      
      if (!logElement) {
        console.error('[VConsole-OutputLog] Could not find log element');
        alert('Could not find logs to copy!');
        return;
      }
      
      let nodeArr = logElement.children;
      console.log(`[VConsole-OutputLog] Found ${nodeArr.length} log entries`);
      
      let _str = '';
      for (let i = 0; i < nodeArr.length; i++) {
        const ele = nodeArr[i];
        _str += `${ele.textContent}\n`;
      }
      
      copy(_str);
      console.log('[VConsole-OutputLog] Logs copied to clipboard successfully');
      alert('Logs copied to clipboard!');
    } catch (e) {
      console.error('[VConsole-OutputLog] Copy failed:', e);
      alert('Error copying logs: ' + e.message);
    }
  };
}

export default VConsoleOutputLogsPlugin; 